import { Logger } from '@frontend/logger';
import { useActiveModal, useModals } from '@frontend/modal';
import { ModuleExtendedClient } from '@frontend/module/api';
import { ModuleExtended } from '@frontend/module/types';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ModulesGridProps {
    spotId: string;
}

interface ViewProps {
    modules: ModuleExtended[][];
    isLoading: boolean;
    viewDetailedModules: (spot_id: string) => Promise<void>;
}

export const useModulesGrid = (props: ModulesGridProps): ViewProps => {
    const [modules, setModules] = useState<ModuleExtended[][]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const { modalId } = useActiveModal();
    const { closeModal } = useModals();

    useEffect(() => {
        if (!props.spotId) return;

        setIsLoading(true);
        ModuleExtendedClient.fetchModuleExtendeds({ spot_id: props.spotId })
            .then((response) => {
                // Group modules by row (location.y) and sort by column (location.x)
                const grouped = response.results.reduce(
                    (acc, module) => {
                        const row = module.location?.y ?? 0;
                        if (!acc[row]) acc[row] = [];
                        acc[row].push(module);
                        return acc;
                    },
                    {} as Record<number, ModuleExtended[]>
                );

                // Sort modules within each row by x coordinate and convert to array
                const sortedRows = Object.keys(grouped)
                    .sort((a, b) => Number(a) - Number(b))
                    .map((row) => grouped[Number(row)].sort((a, b) => (a.location?.x ?? 0) - (b.location?.x ?? 0)));

                setModules(sortedRows);
            })
            .catch((error) => {
                Logger.error('Failed to fetch modules:', error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [props.spotId]);

    async function viewDetailedModules(spot_id: string) {
        await navigate(`/spots/modules?spot_id=${spot_id}`);
        closeModal(modalId);
    }

    return {
        modules,
        isLoading,
        viewDetailedModules
    };
};
